import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, AlertCircle, Book, Home, FileText } from 'lucide-react';
import axios from 'axios';

const Test = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quizSession, setQuizSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(1200); // 20 minutes in seconds
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);

  // Get user data from navigation state
  const userPhone = location.state?.userPhone;
  const loginResponse = location.state?.loginResponse;

  useEffect(() => {
    if (!userPhone) {
      navigate('/testlogin');
      return;
    }
    initializeQuiz();
  }, [userPhone, navigate]);

  // Timer effect
  useEffect(() => {
    if (timeRemaining > 0 && quizSession?.status === 'active') {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timeRemaining, quizSession?.status]);

  const initializeQuiz = async () => {
    try {
      setLoading(true);
      
      // First check if user has an existing session
      const sessionResponse = await axios.get(`https://form-1vqo.onrender.com/api/quiz/session/${userPhone}`);
      
      if (sessionResponse.data.hasSession) {
        const session = sessionResponse.data.session;
        
        // Check if session has ended (completed or submitted)
        if (session.status === 'completed' || session.status === 'submitted') {
          setSessionEnded(true);
          setQuizSession(session);
          setLoading(false);
          return;
        }
      }

      let session = sessionResponse.data.session;
      
      if (!sessionResponse.data.hasSession) {
        // Start new quiz session
        const startResponse = await axios.post('https://form-1vqo.onrender.com/api/quiz/start', {
          phoneNumber: userPhone
        });
        
        if (!startResponse.data.success) {
          setError(startResponse.data.message);
          return;
        }
        
        session = startResponse.data.session;
      }

      // Get questions for the session
      const questionsResponse = await axios.get(`https://form-1vqo.onrender.com/api/quiz/questions/${session._id}`);
      
      if (!questionsResponse.data.success) {
        setError(questionsResponse.data.message);
        return;
      }

      setQuizSession(session);
      setQuestions(questionsResponse.data.questions);
      
      // Set initial answers from session
      const initialAnswers = {};
      questionsResponse.data.questions.forEach(q => {
        if (q.selectedAnswer !== null) {
          initialAnswers[q._id] = q.selectedAnswer;
        }
      });
      setSelectedAnswers(initialAnswers);
      
      // Set remaining time
      if (sessionResponse.data.timeRemaining) {
        setTimeRemaining(Math.floor(sessionResponse.data.timeRemaining / 1000));
      }
      
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to initialize quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = async (questionId, answerIndex) => {
    try {
      setSelectedAnswers(prev => ({
        ...prev,
        [questionId]: answerIndex
      }));

      // Submit answer to backend
      await axios.post('https://form-1vqo.onrender.com/api/quiz/answer', {
        sessionId: quizSession._id,
        questionId,
        selectedAnswer: answerIndex
      });

    } catch (error) {
      console.error('Error submitting answer:', error);
    }
  };

  const handleTimeUp = async () => {
    try {
      await axios.post('https://form-1vqo.onrender.com/api/quiz/submit', {
        sessionId: quizSession._id
      });
      
      navigate('/quiz-result', { 
        state: { 
          userPhone,
          timeUp: true 
        } 
      });
    } catch (error) {
      console.error('Error submitting quiz on timeout:', error);
    }
  };

  const handleSubmitQuiz = async () => {
    try {
      setIsSubmitting(true);
      
      const response = await axios.post('https://form-1vqo.onrender.com/api/quiz/submit', {
        sessionId: quizSession._id
      });

      if (response.data.success) {
        navigate('/quiz-result', { 
          state: { 
            userPhone,
            submitted: true 
          } 
        });
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to submit quiz');
    } finally {
      setIsSubmitting(false);
      setShowConfirmSubmit(false);
    }
  };

  const handleGoHome = () => {
    navigate('/testlogin');
  };

  const handleShowResult = () => {
    navigate('/quiz-result', { 
      state: { 
        userPhone,
        fromSessionEnd: true 
      } 
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getAnsweredCount = () => {
    return Object.keys(selectedAnswers).length;
  };

  const scrollToQuestion = (questionIndex) => {
    const element = document.getElementById(`question-${questionIndex}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  // Show session ended screen
  if (sessionEnded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="bg-green-50 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Quiz Completed</h2>
          <p className="text-gray-600 mb-6">
            You have already completed this quiz. Your session has ended.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleGoHome}
              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white px-6 py-3 rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all"
            >
              <Home className="w-4 h-4" />
              Go Home
            </button>
            <button
              onClick={handleShowResult}
              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all"
            >
              <FileText className="w-4 h-4" />
              Show Result
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="bg-red-50 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Unable to Load Quiz</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/testlogin')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-2 sm:p-3 rounded-full">
                <Book className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Bible Quiz</h1>
                <p className="text-gray-600 text-sm sm:text-base">Welcome, {loginResponse?.data?.fullName}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-xs sm:text-sm text-gray-600">Progress</div>
                <div className="font-semibold text-gray-800 text-sm sm:text-base">
                  {getAnsweredCount()} / {questions.length}
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-xs sm:text-sm text-gray-600">Time Remaining</div>
                <div className={`font-bold text-lg sm:text-xl ${timeRemaining < 300 ? 'text-red-600' : 'text-green-600'}`}>
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1" />
                  {formatTime(timeRemaining)}
                </div>
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-xs sm:text-sm text-gray-600 mb-2">
              <span>Total Questions: {questions.length}</span>
              <span>{Math.round((getAnsweredCount() / questions.length) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(getAnsweredCount() / questions.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Questions Navigation Sidebar */}
          <div className="lg:w-1/4">
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 sticky top-32">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Questions</h3>
              <div className="grid grid-cols-5 sm:grid-cols-5 lg:grid-cols-5 gap-2">
                {questions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => scrollToQuestion(index)}
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg font-medium transition-all text-xs sm:text-sm ${
                      selectedAnswers[question._id] !== undefined
                        ? 'bg-green-100 text-green-700 border-2 border-green-300'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-200'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg">
                <div className="text-sm text-gray-600 mb-2">Summary</div>
                <div className="text-lg font-semibold text-gray-800">
                  {getAnsweredCount()} / {questions.length} Answered
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {questions.length - getAnsweredCount()} Remaining
                </div>
              </div>
            </div>
          </div>

          {/* Questions List */}
          <div className="lg:w-3/4">
            <div className="space-y-6">
              {questions.map((question, index) => (
                <div
                  key={question._id}
                  id={`question-${index}`}
                  className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 scroll-mt-32"
                >
                  <div className="mb-4 sm:mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        Question {index + 1}
                      </span>
                      {selectedAnswers[question._id] !== undefined && (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-800 leading-relaxed">
                      {question.question}
                    </h2>
                  </div>

                  <div className="space-y-3">
                    {question.options.map((option, optionIndex) => (
                      <label
                        key={optionIndex}
                        className={`flex items-start p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedAnswers[question._id] === optionIndex
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`question-${question._id}`}
                          value={optionIndex}
                          checked={selectedAnswers[question._id] === optionIndex}
                          onChange={() => handleAnswerSelect(question._id, optionIndex)}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          selectedAnswers[question._id] === optionIndex
                            ? 'border-purple-500 bg-purple-500'
                            : 'border-gray-300'
                        }`}>
                          {selectedAnswers[question._id] === optionIndex && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <span className="text-gray-700 text-sm sm:text-base">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Submit Section */}
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mt-6 text-center">
              <div className="mb-6">
                <p className="text-gray-600 mb-2 text-sm sm:text-base">
                  You have answered {getAnsweredCount()} out of {questions.length} questions
                </p>
                {getAnsweredCount() < questions.length && (
                  <p className="text-amber-600 text-sm">
                    ⚠️ You have unanswered questions. You can submit now or continue answering.
                  </p>
                )}
              </div>
              
              <button
                onClick={() => setShowConfirmSubmit(true)}
                disabled={isSubmitting}
                className={`w-full sm:w-auto px-8 py-4 rounded-lg font-semibold text-white transition-all ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 transform hover:scale-105 shadow-lg'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Quiz'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmSubmit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 max-w-md w-full">
            <div className="text-center">
              <div className="bg-amber-50 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Submit Quiz?</h3>
              <p className="text-gray-600 mb-6 text-sm sm:text-base">
                Are you sure you want to submit your quiz? This action cannot be undone.
                <br />
                <br />
                <strong>Answered: {getAnsweredCount()} / {questions.length}</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setShowConfirmSubmit(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitQuiz}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg hover:from-green-700 hover:to-blue-700 transition-all"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Test;